import React from "react";
import ReactDOM from "react-dom";
import { Button } from "@material-ui/core";
import { Print } from "@material-ui/icons";
import { css, injectGlobal } from "@emotion/css";
import LinkifiedText from "./LinkifiedText";
import type { ResolvedQuestionnaire } from "../../api";
import type { Page, Part, ResolvedQuestion, ResolvedResponseType, ResolvedQuestionRef } from "../../types/resolved";
import type { Visibility, Requirement } from "../../types/common";
import { primaryText } from "../../types/common";
import type { ExpandedQuestionnaire } from "../../types/persisted";
import { useQuestionnaire } from "../../queries";
import { getResponseOptionTexts } from "../editors/editorUtils";
import { resolveFeelSegments, type Segment } from "../editors/feelResolver";

const PRINT_ROOT = "qd-print-root";
const PRINT_PORTAL = "qd-print-portal";
const PRINT_BODY = "qd-printing-enabled";

const styles = {
  container: css`
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    overflow: hidden;
  `,
  toolbar: css`
    display: flex;
    justify-content: flex-end;
    padding: 8px 16px;
    border-bottom: 1px solid #e0e0e0;
    background: #fafafa;

    @media print {
      display: none;
    }
  `,
  scroll: css`
    flex: 1;
    overflow-y: auto;
    background: #f0f0f0;
    padding: 24px 0 60vh 0;

    @media print {
      overflow: visible;
      background: white;
      padding: 0;
    }
  `,
  sheet: css`
    width: 794px;
    max-width: 100%;
    margin: 0 auto;
    background: white;
    padding: 48px 56px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    color: #222;
    font-size: 13px;
    line-height: 1.5;

    @media print {
      width: auto;
      box-shadow: none;
      padding: 0;
    }
  `,
  title: css`
    font-size: 22px;
    font-weight: 700;
    margin: 0 0 24px 0;
    padding-bottom: 12px;
    border-bottom: 2px solid #222;
  `,
  page: css`
    margin-bottom: 28px;

    &:last-child {
      margin-bottom: 0;
    }
  `,
  pageTitle: css`
    font-size: 16px;
    font-weight: 700;
    margin: 0 0 12px 0;
    padding: 6px 10px;
    background: #f0f0f0;
    border-left: 4px solid #2196f3;
  `,
  group: css`
    margin: 12px 0 16px 0;
    padding: 12px 16px;
    background: #f8f9fa;
    border-left: 3px solid #bbb;
  `,
  groupTitle: css`
    font-size: 14px;
    font-weight: 700;
    margin: 0 0 6px 0;
  `,
  groupDescription: css`
    font-size: 12px;
    color: #555;
    margin: 0 0 12px 0;
  `,
  partBlock: css`
    margin: 14px 0;
    padding-left: 8px;
    border-left: 2px solid transparent;
    break-inside: avoid;

    &:last-child {
      margin-bottom: 0;
    }
  `,
  question: css`
    margin: 0 0 6px 0;
    padding-left: 2px;
    break-inside: avoid;
  `,
  questionText: css`
    font-size: 13px;
    font-weight: 600;
    margin: 0 0 4px 0;
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 8px;
  `,
  optionalPill: css`
    display: inline-block;
    font-size: 10px;
    font-weight: 500;
    font-style: italic;
    color: #6a4b00;
    background: #fff3cd;
    border-radius: 10px;
    padding: 1px 8px;
    letter-spacing: 0.02em;
  `,
  questionDescription: css`
    font-size: 12px;
    color: #555;
    margin: 0 0 6px 0;
  `,
  annotation: css`
    font-size: 11px;
    color: #666;
    font-style: italic;
    margin: 4px 0 0 14px;
    padding-left: 8px;
    border-left: 2px solid #d0d0d0;
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 4px;
    line-height: 1.6;
  `,
  annotationLabel: css`
    flex-shrink: 0;
  `,
  annotationPredicate: css`
    font-style: normal;
    color: #333;
    display: inline-flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 4px;
  `,
  chipQuestion: css`
    background: #e0e0e0;
    border-radius: 3px;
    padding: 1px 5px;
    font-size: 11px;
  `,
  chipOption: css`
    background: #d4edda;
    border-radius: 3px;
    padding: 1px 5px;
    font-size: 11px;
  `,
  chipPatient: css`
    background: #d4e4f7;
    border-radius: 3px;
    padding: 1px 5px;
    font-size: 11px;
  `,
  options: css`
    margin: 6px 0 0 0;
    padding: 0;
    list-style: none;
  `,
  option: css`
    padding: 2px 0;
    font-size: 12px;
  `,
  optionParentLabel: css`
    font-weight: 600;
    margin: 6px 0 2px 0;
    font-size: 12px;
  `,
  optionIndent: css`
    padding-left: 18px;
  `,
  scale: css`
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 4px 0;
    flex-wrap: wrap;
  `,
  scaleBox: css`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 22px;
    height: 22px;
    padding: 0 4px;
    border: 1px solid #999;
    border-radius: 3px;
    font-size: 11px;
    background: white;
  `,
  scaleLabel: css`
    font-size: 11px;
    color: #555;
  `,
  inputBox: css`
    display: inline-block;
    min-width: 140px;
    height: 22px;
    border: 1px solid #999;
    border-radius: 3px;
    background: white;
    vertical-align: middle;
    margin: 2px 0;
  `,
  textareaBox: css`
    display: block;
    min-height: 60px;
    border: 1px solid #999;
    border-radius: 3px;
    background: white;
    margin: 4px 0;
  `,
  emptyPage: css`
    font-size: 12px;
    color: #888;
    font-style: italic;
  `,
  printPortal: css`
    display: none;
    @media print {
      display: block;
    }
  `,
  printSheet: css`
    color: #222;
    font-size: 12px;
    line-height: 1.5;
  `,
};

injectGlobal`
  @media print {
    @page {
      size: A4;
      margin: 16mm;
      @top-left { content: ""; }
      @top-center { content: ""; }
      @top-right { content: ""; }
      @bottom-left { content: ""; }
      @bottom-center { content: ""; }
      @bottom-right {
        content: counter(page) " / " counter(pages);
        font-size: 9pt;
        color: #666;
      }
    }
    html, body.${PRINT_BODY} {
      background: white !important;
    }
    body.${PRINT_BODY} > *:not(.${PRINT_PORTAL}) {
      display: none !important;
    }
    body.${PRINT_BODY} .${PRINT_PORTAL} .${PRINT_ROOT} {
      padding: 0 !important;
      box-shadow: none !important;
      width: auto !important;
      max-width: none !important;
    }
  }
`;

type FeelMaps = { question: Record<string, string>; option: Record<string, string> };

const FeelMapsContext = React.createContext<FeelMaps>({ question: {}, option: {} });

const buildFeelMapsFromResolved = (questionnaire: ResolvedQuestionnaire): FeelMaps => {
  const question: Record<string, string> = {};
  const option: Record<string, string> = {};
  const addOptions = (rt: ResolvedResponseType | null) => {
    if (!rt) return;
    if (
      rt._type === "select" ||
      rt._type === "select-or-other" ||
      rt._type === "multi-select" ||
      rt._type === "multi-select-or-other"
    ) {
      for (const v of rt.variants) if (v.feel_id) option[v.feel_id] = v.text;
    } else if (rt._type === "multi-select-grouped") {
      for (const g of rt.groups) for (const v of g.variants) if (v.feel_id) option[v.feel_id] = v.text;
    }
  };
  const addQuestion = (q: ResolvedQuestion) => {
    if (q.feel_id) question[q.feel_id] = q.question;
    addOptions(q.response_type);
  };
  for (const page of questionnaire.pages) {
    for (const part of page.parts) {
      if (part._type === "question") addQuestion(part.question);
      else for (const qRef of part.question_group.questions) addQuestion(qRef.question);
    }
  }
  return { question, option };
};

const buildFeelMapsFromExpanded = (questionnaire: ExpandedQuestionnaire): FeelMaps => {
  const question: Record<string, string> = {};
  const option: Record<string, string> = {};
  for (const page of questionnaire.pages) {
    for (const part of page.parts) {
      if (part._type === "question" && part.question?.feel_id) {
        if (part.question.question) question[part.question.feel_id] = primaryText(part.question.question);
        if (part.question.response_type) Object.assign(option, getResponseOptionTexts(part.question.response_type));
      }
      if (part._type === "question_group" && part.question_group) {
        for (const qRef of part.question_group.questions) {
          if (qRef.question?.feel_id) {
            if (qRef.question.question) question[qRef.question.feel_id] = primaryText(qRef.question.question);
            if (qRef.question.response_type) Object.assign(option, getResponseOptionTexts(qRef.question.response_type));
          }
        }
        for (const dep of part.question_group.dependencies ?? []) {
          if (dep.feel_id && dep.question) question[dep.feel_id] = primaryText(dep.question);
          if (dep.response_type) Object.assign(option, getResponseOptionTexts(dep.response_type));
        }
      }
    }
  }
  return { question, option };
};

const renderSegments = (segments: Segment[]) =>
  segments.map((s, i) => {
    if (s.type === "question")
      return (
        <span key={i} className={styles.chipQuestion}>
          {s.value}
        </span>
      );
    if (s.type === "option")
      return (
        <span key={i} className={styles.chipOption}>
          "{s.value}"
        </span>
      );
    if (s.type === "patient")
      return (
        <span key={i} className={styles.chipPatient}>
          {s.value}
        </span>
      );
    if (s.type === "linebreak") return <br key={i} />;
    return <span key={i}>{s.value}</span>;
  });

const Predicate: React.VFC<{ predicate: string }> = ({ predicate }) => {
  const maps = React.useContext(FeelMapsContext);
  const segments = React.useMemo(() => resolveFeelSegments(predicate, maps.question, maps.option), [predicate, maps]);
  return <span className={styles.annotationPredicate}>{renderSegments(segments)}</span>;
};

const Annotations: React.VFC<{ visibility: Visibility; requirement?: Requirement }> = ({ visibility, requirement }) => {
  const visiblePredicate = visibility === "visible" ? null : visibility.visibleWhen;
  const requiredPredicate = typeof requirement === "object" ? requirement.requiredWhen : null;
  if (!visiblePredicate && !requiredPredicate) return null;
  return (
    <>
      {visiblePredicate && (
        <div className={styles.annotation}>
          <span className={styles.annotationLabel}>Shown when</span>
          <Predicate predicate={visiblePredicate} />
        </div>
      )}
      {requiredPredicate && (
        <div className={styles.annotation}>
          <span className={styles.annotationLabel}>Required when</span>
          <Predicate predicate={requiredPredicate} />
        </div>
      )}
    </>
  );
};

const ResponseTypeView: React.VFC<{ responseType: ResolvedResponseType }> = ({ responseType }) => {
  switch (responseType._type) {
    case "select":
    case "select-or-other":
    case "multi-select":
    case "multi-select-or-other": {
      const marker = responseType._type.startsWith("multi") ? "☐" : "○";
      const hasOther = responseType._type.endsWith("-or-other");
      const groups: { label: string | null; variants: typeof responseType.variants }[] = [];
      for (const v of responseType.variants) {
        const label = v.parent_label ?? null;
        const last = groups[groups.length - 1];
        if (last && last.label === label) last.variants.push(v);
        else groups.push({ label, variants: [v] });
      }
      return (
        <ul className={styles.options}>
          {groups.map((group, gi) => (
            <React.Fragment key={gi}>
              {group.label && <li className={styles.optionParentLabel}>{group.label}</li>}
              {group.variants.map(v => (
                <li key={v.id} className={`${styles.option} ${group.label ? styles.optionIndent : ""}`}>
                  {marker} <LinkifiedText text={v.text} />
                </li>
              ))}
            </React.Fragment>
          ))}
          {hasOther && (
            <li className={styles.option}>
              {marker} Other: <span className={styles.inputBox} />
            </li>
          )}
        </ul>
      );
    }
    case "multi-select-grouped":
      return (
        <ul className={styles.options}>
          {responseType.groups.map((g, gi) => (
            <React.Fragment key={gi}>
              <li className={styles.optionParentLabel}>{g.label}</li>
              {g.variants.map(v => (
                <li key={v.id} className={`${styles.option} ${styles.optionIndent}`}>
                  ☐ <LinkifiedText text={v.text} />
                </li>
              ))}
            </React.Fragment>
          ))}
        </ul>
      );
    case "checkbox":
      return <div className={styles.option}>☐ Yes / no</div>;
    case "number-range":
    case "vas": {
      const min = responseType._type === "vas" ? responseType.min : responseType.min.value;
      const max = responseType._type === "vas" ? responseType.max : responseType.max.value;
      const minLabel = responseType._type === "number-range" ? responseType.min.label : undefined;
      const maxLabel = responseType._type === "number-range" ? responseType.max.label : undefined;
      const count = max - min + 1;
      const showBoxes = count <= 12;
      return (
        <div>
          <div className={styles.scale}>
            {minLabel && <span className={styles.scaleLabel}>{minLabel}</span>}
            {showBoxes ? (
              Array.from({ length: count }, (_, i) => (
                <span key={i} className={styles.scaleBox}>
                  {min + i}
                </span>
              ))
            ) : (
              <span className={styles.scaleLabel}>
                {min} – {max}
              </span>
            )}
            {maxLabel && <span className={styles.scaleLabel}>{maxLabel}</span>}
          </div>
        </div>
      );
    }
    case "integer":
      return (
        <div className={styles.option}>
          <span className={styles.inputBox} />{" "}
          <span className={styles.scaleLabel}>
            ({responseType.min} – {responseType.max})
          </span>
        </div>
      );
    case "date":
      return (
        <div className={styles.option}>
          <span className={styles.inputBox} /> <span className={styles.scaleLabel}>(date)</span>
        </div>
      );
    case "text":
      return responseType.multiline ? (
        <span className={styles.textareaBox} />
      ) : (
        <div className={styles.option}>
          <span className={styles.inputBox} />
          {responseType.max_length != null && (
            <span className={styles.scaleLabel}> (max {responseType.max_length} chars)</span>
          )}
        </div>
      );
    case "text-area":
      return <span className={styles.textareaBox} />;
    default:
      return null;
  }
};

const isOptionalReq = (req?: Requirement) => req === "optional";

const QuestionView: React.VFC<{ question: ResolvedQuestion; isOptional: boolean }> = ({ question, isOptional }) => (
  <div className={styles.question}>
    <div className={styles.questionText}>
      <span>
        <LinkifiedText text={question.question} />
      </span>
      {isOptional && <span className={styles.optionalPill}>optional</span>}
    </div>
    {question.description && (
      <div className={styles.questionDescription}>
        <LinkifiedText text={question.description} />
      </div>
    )}
    {question.response_type && <ResponseTypeView responseType={question.response_type} />}
  </div>
);

const QuestionRefView: React.VFC<{ qRef: ResolvedQuestionRef }> = ({ qRef }) => (
  <div className={styles.partBlock}>
    <QuestionView question={qRef.question} isOptional={isOptionalReq(qRef.requirement)} />
    <Annotations visibility={qRef.visibility} requirement={qRef.requirement} />
  </div>
);

const PartView: React.VFC<{ part: Part }> = ({ part }) => {
  if (part._type === "question") {
    return (
      <div className={styles.partBlock}>
        <QuestionView question={part.question} isOptional={isOptionalReq(part.requirement)} />
        <Annotations visibility={part.visibility} requirement={part.requirement} />
      </div>
    );
  }
  const group = part.question_group;
  return (
    <div className={`${styles.group} ${styles.partBlock}`}>
      {group.title && <div className={styles.groupTitle}>{group.title}</div>}
      {group.description && (
        <div className={styles.groupDescription}>
          <LinkifiedText text={group.description} />
        </div>
      )}
      {group.questions.map((qRef, i) => (
        <QuestionRefView key={i} qRef={qRef} />
      ))}
      <Annotations visibility={part.visibility} requirement={part.requirement} />
    </div>
  );
};

const pageDomId = (qid: number, index: number) => `qd-page-${qid}-${index}`;

const PageView: React.VFC<{ page: Page; index: number; domId?: string }> = ({ page, index, domId }) => (
  <section className={styles.page} id={domId}>
    <h2 className={styles.pageTitle}>
      {index + 1}. {page.title || "Untitled"}
    </h2>
    {page.parts.length === 0 ? (
      <div className={styles.emptyPage}>(no questions)</div>
    ) : (
      page.parts.map((part, i) => <PartView key={i} part={part} />)
    )}
  </section>
);

const DocumentSheet: React.VFC<{ questionnaire: ResolvedQuestionnaire; withPageIds: boolean }> = ({
  questionnaire,
  withPageIds,
}) => (
  <article className={`${styles.sheet} ${PRINT_ROOT}`}>
    <h1 className={styles.title}>{questionnaire.name}</h1>
    {questionnaire.pages.map((page, i) => (
      <PageView key={i} page={page} index={i} domId={withPageIds ? pageDomId(questionnaire.id, i) : undefined} />
    ))}
  </article>
);

export const QuestionnaireDocument: React.VFC<{ questionnaire: ResolvedQuestionnaire; currentPageIndex?: number }> = ({
  questionnaire,
  currentPageIndex,
}) => {
  const { data: expanded } = useQuestionnaire(questionnaire.id);
  const feelMaps = React.useMemo(
    () => (expanded ? buildFeelMapsFromExpanded(expanded) : buildFeelMapsFromResolved(questionnaire)),
    [expanded, questionnaire]
  );
  const scrollRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    document.body.classList.add(PRINT_BODY);
    return () => {
      document.body.classList.remove(PRINT_BODY);
    };
  }, []);
  React.useEffect(() => {
    if (currentPageIndex == null) return;
    const target = document.getElementById(pageDomId(questionnaire.id, currentPageIndex));
    const scroller = scrollRef.current;
    if (!target || !scroller) return;
    const targetRect = target.getBoundingClientRect();
    const scrollerRect = scroller.getBoundingClientRect();
    const top = scroller.scrollTop + (targetRect.top - scrollerRect.top) - 8;
    scroller.scrollTo({ top, behavior: "smooth" });
  }, [currentPageIndex, questionnaire.id, questionnaire.pages.length]);
  return (
    <FeelMapsContext.Provider value={feelMaps}>
      <div className={styles.container}>
        <div className={styles.toolbar}>
          <Button size="small" variant="outlined" startIcon={<Print />} onClick={() => window.print()}>
            Save as PDF
          </Button>
        </div>
        <div className={styles.scroll} ref={scrollRef}>
          <DocumentSheet questionnaire={questionnaire} withPageIds />
        </div>
      </div>
      {ReactDOM.createPortal(
        <div className={`${styles.printPortal} ${PRINT_PORTAL}`}>
          <DocumentSheet questionnaire={questionnaire} withPageIds={false} />
        </div>,
        document.body
      )}
    </FeelMapsContext.Provider>
  );
};
